import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'

interface Props {
  isOpen: boolean
  isLoading: boolean
  categoryTitle: string
  totalProducts: number
  hiddenProductsCount: number
  nextHiddenState: boolean
  onClose: () => void
  onConfirm: () => void
}

export const CategoryConfirmModal: React.FC<Props> = ({
  isOpen,
  isLoading,
  categoryTitle,
  totalProducts,
  hiddenProductsCount,
  nextHiddenState,
  onClose,
  onConfirm
}) => {
  const actionLabel = nextHiddenState ? 'mask' : 'show'
  const confirmLabel = nextHiddenState ? 'Mask category' : 'Show category'

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={isLoading ? () => undefined : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  {nextHiddenState ? 'Mask this category?' : 'Show this category?'}
                </Dialog.Title>

                <div className="mt-4 space-y-3 text-sm text-gray-600">
                  <p>
                    <span className="font-semibold text-gray-900">{categoryTitle}</span>
                  </p>
                  <p>Confirming will {actionLabel} all products in this category from the public menu in one action.</p>
                  <div className="rounded-md bg-gray-50 p-4 text-gray-700">
                    <p>
                      {totalProducts} product{totalProducts > 1 ? 's' : ''}
                    </p>
                    <p>
                      {hiddenProductsCount}/{totalProducts} already offline
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    disabled={isLoading}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                      nextHiddenState ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                    }`}
                    onClick={onConfirm}
                  >
                    {isLoading ? 'Saving...' : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
